import React, { useState } from "react";
import {
  Collapse,
  Button,
  Tag,
  Pagination,
  Empty,
  Spin,
  Modal,
  Input,
} from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { LoadingOutlined } from "@ant-design/icons";
import { getDistanceWithFallback } from "../../../utils/distance";

const PendingOrdersList = ({
  orders,
  loading,
  pagination,
  onAccept,
  updateFilters,
}) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [acceptModalVisible, setAcceptModalVisible] = useState(false);

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const handleAccept = async (orderId) => {
    setLoadingStates((prev) => ({ ...prev, [orderId]: true }));
    try {
      await onAccept(orderId);
      setAcceptModalVisible(false);
      setSelectedOrder(null);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateFilters({ page: 1, search: value });
  };

  const handlePaginationChange = (page, pageSize) => {
    updateFilters({ page, limit: pageSize });
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setAcceptModalVisible(true);
  };

  if (loading) {
    return <Spin indicator={newAntIcon} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Input
          placeholder="Search by order ref..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={handleSearch}
          allowClear
        />
      </div>

      {orders && orders.length > 0 ? (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 p-3 text-left">
                    Order Ref
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Restaurant
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Contact
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Pick-up Address
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Distance
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Delivery Fee
                  </th>
                  <th className="border border-gray-300 p-3 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.order.id}
                    className="border border-gray-300 hover:bg-gray-50"
                  >
                    <td className="border border-gray-300 p-3 font-mono text-lg font-extrabold text-gray-900">
                      {order.order?.orderCode || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.business?.name || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.business?.phone || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      {order.order?.pickupAddress || order.pickupAddress || order.business?.pickupAddressString || order.business?.address || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {getDistanceWithFallback(order.delivery?.trackerLink, order.delivery?.distance)} km
                    </td>
                    <td className="border border-gray-300 p-3">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix="₦"
                        displayType="text"
                        value={order.deliveryFee || 0}
                      />
                    </td>
                    <td className="border border-gray-300 p-3 text-center">
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <Collapse defaultActiveKey={orders.length > 0 ? [orders[0].order.id] : []}>
              {orders.map((order) => (
                <Collapse.Panel
                  key={order.order.id}
                  header={
                    <div className="flex justify-between items-center w-full">
                      <span className="font-extrabold text-lg text-gray-900">
                        #{order.order?.orderCode}
                      </span>
                      <span className="text-sm text-gray-600">
                        {order.business?.name}
                      </span>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 min-w-fit">
                        Order Ref:
                      </span>
                      <span className="font-extrabold text-lg text-gray-900">
                        #{order.order?.orderCode}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 min-w-fit">
                        Restaurant:
                      </span>
                      <span className="font-medium">
                        {order.business?.name}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <PhoneOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">Contact:</div>
                        <div className="font-medium">
                          {order.business?.phone || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <EnvironmentOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">
                          Pick-up Address:
                        </div>
                        <div className="font-medium">
                          {order.order?.pickupAddress || order.pickupAddress || order.business?.pickupAddressString || order.business?.address || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <EnvironmentOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">
                          Distance:
                        </div>
                        <div className="font-medium">
                          {getDistanceWithFallback(order.delivery?.trackerLink, order.delivery?.distance)} km
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <DollarOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">
                          Delivery Fee:
                        </div>
                        <div className="font-medium">
                          <NumberFormat
                            thousandSeparator={true}
                            prefix="₦"
                            displayType="text"
                            value={parseFloat(
                              order?.deliveryFeeEarnings?.deliveryFeeKonnect ||
                              0,
                            ).toFixed(2)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button
                        type="primary"
                        block
                        onClick={() => handleViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View Order
                      </Button>
                    </div>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>

          <Pagination
            current={pagination?.page || 1}
            pageSize={pagination?.limit || 10}
            total={pagination?.total || 0}
            onChange={handlePaginationChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`
            }
            style={{ marginTop: 16, textAlign: "center" }}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No pending orders available"
        />
      )}

      {/* Accept Modal */}
      <Modal
        title="Accept Delivery Order"
        open={acceptModalVisible}
        onCancel={() => setAcceptModalVisible(false)}
        footer={null}
        centered
        width={500}
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-lg font-bold text-gray-900 mb-1">
                {selectedOrder.order?.orderCode}
              </div>
              <div className="text-sm text-gray-600">
                {selectedOrder.business?.name}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Contact:</span>
                <span className="font-medium">
                  {selectedOrder.business?.phone}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pickup Address:</span>
                <span className="font-medium text-right">
                  {selectedOrder.order?.pickupAddress || selectedOrder.pickupAddress || selectedOrder.business?.pickupAddressString || selectedOrder.business?.address}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Address:</span>
                <span className="font-medium text-right">
                  {selectedOrder.savedAddress?.address}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Distance:</span>
                <span className="font-medium text-right">
                  {getDistanceWithFallback(selectedOrder.delivery?.trackerLink, selectedOrder.delivery?.distance)} km
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="text-gray-600 font-semibold">
                  Delivery Fee:
                </span>
                <span className="font-bold text-green-600 text-lg">
                  <NumberFormat
                    thousandSeparator={true}
                    prefix="₦"
                    displayType="text"
                    value={parseFloat(
                      selectedOrder?.deliveryFeeEarnings?.deliveryFeeKonnect ||
                      0,
                    ).toFixed(2)}
                  />
                </span>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              block
              loading={loadingStates[selectedOrder.order.id]}
              onClick={() => handleAccept(selectedOrder.order.id)}
              icon={<CheckCircleOutlined />}
              className="bg-green-600 hover:bg-green-700 border-green-600"
            >
              Accept Delivery
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PendingOrdersList;
